import React, { useEffect } from "react";
import { EditOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input } from "antd";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import UploadFoodImage from "../../../lib/UploadFoodImage";

interface HowItWorksProps {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  form: any;
  howItWorksData?: {
    heading?: string;
    description?: string;
    cards?: {
      icon?: string;
      title?: string;
      description?: string;
    }[];
  };
  loading?: boolean;
}

const HowItWorks: React.FC<HowItWorksProps> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  howItWorksData,
  loading,
}) => {
  useEffect(() => {
    if (howItWorksData) {
      form.setFieldsValue({
        howItWorks: {
          heading: howItWorksData.heading || "",
          description: howItWorksData.description || "",
          cards: howItWorksData.cards?.map((card: any) => ({
            ...card,
            icon: card.icon ? [card.icon] : [],
          })),
        },
      });
    } else {
      form.resetFields();
    }
  }, [howItWorksData, form]);

  return (
    <div>
      {!isEditing ? (
        <>
          {/* Desktop Table */}
          <div className="rounded-[20px] overflow-hidden hidden sm:block">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-primary text-white">
                  {/* <th className="py-3 px-4 text-left text-sm md:text-base">
                    Image
                  </th> */}
                  <th className="py-3 px-4 text-center text-sm md:text-base">
                    Heading
                  </th>
                  <th className="py-3 px-4 text-center text-sm md:text-base">
                    Description
                  </th>
                  <th className="py-3 px-4 text-end text-sm md:text-base">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {howItWorksData ? (
                  <tr className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]">
                    {/* <td className="py-3 px-4 w-[120px]">
                      <Image
                        src={howItWorksData?. || "/placeholder.jpg"}
                        height={50}
                        width={120}
                        className="object-contain w-full h-full"
                        alt="How It Works"
                      />
                    </td> */}

                    <td className="py-3 px-4 text-center text-gray-700">
                      {howItWorksData?.heading || "—"}
                    </td>

                    <td className="py-3 px-4 text-center text-gray-700">
                      {howItWorksData?.description || "—"}
                    </td>

                    <td className="py-3 px-4 text-primary">
                      <div className="flex items-center justify-end gap-4">
                        <EditOutlined
                          onClick={() => setIsEditing(true)}
                          className="cursor-pointer hover:text-blue-600 text-[20px]"
                        />
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]">
                    <td colSpan={4} className="text-center py-6 text-gray-500">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile View */}
          <div className="sm:hidden">
            {howItWorksData ? (
              <div className="bg-[#F9F9F9] border border-gray-200 rounded-lg p-4 shadow-sm space-y-3">
                {/* <div>
                  <span className="font-semibold text-primary block mb-1">
                    Image:
                  </span>
                  <Image
                    src={howItWorksData?.image || "/placeholder.jpg"}
                    height={70}
                    width={70}
                    alt="How It Works"
                  />
                </div> */}

                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Heading:
                  </span>
                  <p>{howItWorksData?.heading || "—"}</p>
                </div>

                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Description:
                  </span>
                  <p>{howItWorksData?.description || "—"}</p>
                </div>

                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Action:
                  </span>
                  <EditOutlined
                    onClick={() => setIsEditing(true)}
                    className="cursor-pointer text-black text-[18px]"
                  />
                </div>
              </div>
            ) : (
              <div className="bg-[#F9F9F9] border flex justify-center border-gray-200 rounded-lg p-4 shadow-sm">
                <Button type="dashed" onClick={() => setIsEditing(true)}>
                  Add Data
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Edit Mode */
        <div>
          <div className="bg-[#F9F9F9] p-4 sm:p-6 rounded-lg sm:rounded-2xl shadow-sm">
            <Form layout="vertical" form={form}>
              <Form.Item
                label="Heading"
                name={["howItWorks", "heading"]}
                rules={[{ required: true, message: "Please enter heading" }]}
              >
                <Input placeholder="Enter heading" />
              </Form.Item>

              <Form.Item
                label="Description"
                name={["howItWorks", "description"]}
                rules={[
                  { required: true, message: "Please enter description" },
                ]}
              >
                <Input placeholder="Enter description" />
              </Form.Item>

              <Form.List name={["howItWorks", "cards"]}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <div
                        key={key}
                        className="border p-4 rounded-lg mb-4 bg-white"
                      >
                        <Form.Item
                          {...restField}
                          label="Icon"
                          name={[name, "icon"]}
                          rules={[{ required: true, message: "Upload icon" }]}
                        >
                          <UploadFoodImage maxUploadCount={1} bannerAspect={1} />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          label="Title"
                          name={[name, "title"]}
                          rules={[{ required: true, message: "Enter title" }]}
                        >
                          <Input placeholder="Enter title" />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          label="Description"
                          name={[name, "description"]}
                          rules={[{ required: true, message: "Enter description" }]}
                        >
                          <Input.TextArea rows={3} />
                        </Form.Item>

                        <Button danger onClick={() => remove(name)}>
                          Remove
                        </Button>
                      </div>
                    ))}

                    <Button type="dashed" onClick={() => add()} >
                      Add Card
                    </Button>
                  </>
                )}
              </Form.List>

            </Form>
          </div>

          <div className="flex justify-start gap-3 mt-6 sticky bottom-5">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>

            <ButtonWrapper onClick={handleCancel}>
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default HowItWorks;